import type { PasteDetail, SessionUser, VersionDetail } from '../../shared/types'
import { ApiError, deletePaste, fetchPaste } from '../api'
import { renderDiagrams } from '../diagrams'
import { unifiedDiff } from '../diff'
import { createEditor, type EditorHandle } from '../editor'
import { buildFrame } from '../frame'
import { languageLabel } from '../languages'
import { renderMarkdown } from '../markdown'
import { describeCommand, fetchCommand } from '../oneliner'
import { forgetPalettePastas } from '../palette'
import { internalLink, navigate, reload } from '../router'
import { historyPanel, type HistoryPanel } from './history'
import { saucePicker } from '../saucepicker'
import { copyText, formatBytes, formatCountdown, formatRelative, h, plural, toast } from '../ui'

type Mode = 'rendered' | 'source' | 'diff'

export function pasteView(root: HTMLElement, id: string, user: SessionUser | null): () => void {
  const frame = buildFrame({ user, subbar: true })
  frame.stage.append(h('div', { class: 'notice' }, [h('p', {}, ['Plating…'])]))
  frame.statusLeft.textContent = id
  root.append(frame.element)
  document.title = `${id} · pastabin`

  let editor: EditorHandle | null = null
  let diffEditor: EditorHandle | null = null
  let history: HistoryPanel | null = null
  let timer: number | null = null
  let disposed = false

  void fetchPaste(id)
    .then((paste) => {
      if (!disposed) render(paste)
    })
    .catch((error: unknown) => {
      if (!disposed) fail(error)
    })

  function render(paste: PasteDetail): void {
    frame.stage.replaceChildren()
    document.title = `${paste.title ?? id} · pastabin`

    if (paste.burned) {
      frame.insertBelowBar(
        h('div', { class: 'banner' }, [
          h('b', {}, ['One serving.']),
          h('span', {}, ['This pasta has been eaten — copy it now, the link is already dead.']),
        ]),
      )
    }

    const isMarkdown = paste.language === 'markdown'
    let mode: Mode = isMarkdown ? 'rendered' : 'source'

    const historyButton = h(
      'button',
      {
        class: 'control',
        type: 'button',
        'aria-pressed': 'false',
        title: 'What this pasta used to say',
        onclick: () => toggleHistory(),
      },
      ['History'],
    )

    const prose = h('article', { class: 'prose' })
    const proseScroll = h('div', { class: 'prose-scroll', hidden: !isMarkdown }, [prose])
    const editorHost = h('div', { class: 'editor', hidden: isMarkdown })
    const diffHost = h('div', { class: 'editor', hidden: true })
    frame.stage.append(proseScroll, editorHost, diffHost)

    const renderedButton = h(
      'button',
      { type: 'button', 'aria-pressed': 'true', onclick: () => setMode('rendered') },
      ['Rendered'],
    )
    const sourceButton = h(
      'button',
      { type: 'button', 'aria-pressed': 'false', onclick: () => setMode('source') },
      ['Source'],
    )

    if (paste.title) {
      frame.controls.append(h('span', { class: 'view-title', title: paste.title }, [paste.title]))
    }

    if (isMarkdown) {
      frame.controls.append(
        h('div', { class: 'segmented', role: 'group', 'aria-label': 'View mode' }, [
          renderedButton,
          sourceButton,
        ]),
      )
    }

    frame.controls.append(
      h(
        'button',
        {
          class: 'control',
          type: 'button',
          onclick: async () => {
            const copied = await copyText(paste.content)
            toast(copied ? 'Pasta copied' : 'Could not copy')
          },
        },
        ['Copy'],
      ),
      h('a', { class: 'control', href: `/raw/${id}`, target: '_blank', rel: 'noopener' }, ['Raw']),
      oneLinerButton(paste),
    )

    if (paste.owned) {
      frame.controls.append(
        h('a', { class: 'control', ...internalLink(`/e/${id}`) }, ['Edit']),
        historyButton,
        h('button', { class: 'control', type: 'button', onclick: () => void remove() }, ['Delete']),
      )
    }

    frame.controls.append(
      saucePicker(() => {
        editor?.refreshTheme()
        redrawDiagrams()
      }),
      h('a', { class: 'control primary', ...internalLink('/') }, ['New pasta']),
    )

    // Created lazily: a CodeMirror instance mounted into a hidden container
    // measures itself at zero width and lays out wrong when revealed.
    function ensureEditor(): void {
      if (editor) return
      editor = createEditor({
        parent: editorHost,
        doc: paste.content,
        language: paste.language,
        readOnly: true,
      })
    }

    function setMode(next: Mode): void {
      mode = next
      proseScroll.hidden = next !== 'rendered'
      editorHost.hidden = next !== 'source'
      diffHost.hidden = next !== 'diff'
      renderedButton.setAttribute('aria-pressed', String(next === 'rendered'))
      sourceButton.setAttribute('aria-pressed', String(next === 'source'))
      if (next === 'source') ensureEditor()
    }

    /** The mode to fall back to when the diff is dismissed. */
    const textMode: Mode = isMarkdown ? 'rendered' : 'source'

    /**
     * Shows what changed between an old revision and the text that's current,
     * as a unified diff in the `diff` grammar the language list already has.
     */
    function showVersion(version: VersionDetail | null): void {
      if (!version) {
        setMode(textMode)
        return
      }

      const patch = unifiedDiff(
        version.content,
        paste.content,
        {
          before: `${version.title ?? id} · ${formatRelative(version.replacedAt)}`,
          after: `${paste.title ?? id} · current`,
        },
      )

      diffEditor?.destroy()
      diffEditor = null
      diffHost.replaceChildren()

      if (!patch) {
        diffHost.append(
          h('div', { class: 'notice' }, [
            h('p', {}, ['That version is identical to the current text.']),
          ]),
        )
      } else {
        diffEditor = createEditor({
          parent: diffHost,
          doc: patch,
          language: 'diff',
          readOnly: true,
        })
      }
      setMode('diff')
    }

    function toggleHistory(): void {
      if (history) {
        history.destroy()
        history = null
        historyButton.setAttribute('aria-pressed', 'false')
        showVersion(null)
        return
      }
      history = historyPanel({
        pasteId: id,
        onSelect: showVersion,
        onRestored: () => {
          forgetPalettePastas()
          // The pasta changed underneath this view; re-run the route rather
          // than patch half a dozen places that cached the old content.
          reload()
        },
      })
      historyButton.setAttribute('aria-pressed', 'true')
      frame.insertBelowBar(history.element)
    }

    // Kept so a sauce change can re-run the diagrams against clean markup
    // instead of trying to recolour SVG that mermaid already emitted.
    let proseHtml = ''

    function redrawDiagrams(): void {
      if (!proseHtml || !prose.querySelector('.diagram')) return
      prose.innerHTML = proseHtml
      void renderDiagrams(prose)
    }

    if (isMarkdown) {
      prose.append(h('p', { class: 'muted' }, ['Rendering…']))
      void renderMarkdown(paste.content)
        .then((html) => {
          if (disposed) return
          // Sanitised in markdown.ts: markdown-it with html:false, then DOMPurify.
          proseHtml = html
          prose.innerHTML = html
          // Diagrams settle in after the prose: mermaid loads on demand, and a
          // failure here leaves the fenced source on screen.
          return renderDiagrams(prose)
        })
        .catch(() => {
          if (disposed) return
          setMode('source')
          toast('Could not render the markdown — showing source')
        })
    } else {
      ensureEditor()
    }

    setMode(mode)

    frame.statusLeft.textContent = [
      languageLabel(paste.language),
      formatBytes(paste.sizeBytes),
      plural(paste.content.split('\n').length, 'line'),
      paste.visibility === 'public' ? 'public link' : 'private',
    ].join(' · ')

    const age = h('span', { class: 'hide-sm' }, [`served ${formatRelative(paste.createdAt)}`])
    const expiry = h('span', {}, [])
    frame.statusRight.append(age, expiry)

    const tick = (): void => {
      age.textContent =
        paste.updatedAt > paste.createdAt
          ? `edited ${formatRelative(paste.updatedAt)}`
          : `served ${formatRelative(paste.createdAt)}`
      if (paste.burned) {
        expiry.textContent = 'gone'
        expiry.className = 'warn'
        expiry.title = 'One-serving pasta — deleted when it was opened'
        return
      }
      if (paste.expiresAt === null) {
        expiry.textContent = 'never expires'
        expiry.title = 'Kept until deleted'
        return
      }
      const remaining = paste.expiresAt - Date.now()
      expiry.textContent = remaining <= 0 ? 'expired' : `expires in ${formatCountdown(paste.expiresAt)}`
      expiry.title = new Date(paste.expiresAt).toLocaleString()
      expiry.className = remaining <= 60_000 ? 'warn' : ''
    }

    tick()
    timer = window.setInterval(tick, 1000)

    async function remove(): Promise<void> {
      if (!confirm(`Delete “${paste.title ?? id}”? This can't be undone.`)) return
      try {
        await deletePaste(id)
        forgetPalettePastas()
        toast('Deleted')
        navigate('/mine')
      } catch (error) {
        toast(error instanceof ApiError ? error.message : 'Could not delete that pasta')
      }
    }
  }

  function fail(error: unknown): void {
    const gone = error instanceof ApiError && error.status === 404
    frame.stage.replaceChildren(
      h('div', { class: 'notice' }, [
        h('h1', {}, [gone ? 'This dish has been eaten' : 'Could not fetch that pasta']),
        h('p', {}, [
          gone
            ? user
              ? "It expired, it was a one-serving pasta someone already opened, or it's private and not yours."
              : "It expired, it was a one-serving pasta someone already opened, or it's private — try signing in."
            : error instanceof ApiError
              ? error.message
              : 'Something went wrong on the way to the kitchen.',
        ]),
        h('div', { class: 'actions' }, [
          h('a', { class: 'control primary', ...internalLink('/') }, ['Start a new pasta']),
        ]),
      ]),
    )
    frame.statusLeft.textContent = gone ? 'gone' : 'error'
    frame.statusLeft.className = 'left warn'
  }

  return () => {
    disposed = true
    if (timer !== null) clearInterval(timer)
    editor?.destroy()
    diffEditor?.destroy()
    history?.destroy()
  }
}

/** One click puts the fetch-this-pasta command on the clipboard. */
function oneLinerButton(paste: PasteDetail): HTMLElement {
  const command = fetchCommand(paste, location.origin)
  return h(
    'button',
    {
      class: 'control',
      type: 'button',
      title: describeCommand(command),
      onclick: async () => {
        const copied = await copyText(command.command)
        toast(copied ? `${command.tool} command copied` : 'Could not copy')
      },
    },
    [command.tool],
  )
}
